import axios from 'axios'
import store from '../store'
import firebase from 'firebase/app'
import qs from 'qs'
import { get } from 'lodash'

const request = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 10000,
})

request.interceptors.request.use(async (config) => {
  if (firebase.auth().currentUser)
    config.headers.authorization = await firebase.auth().currentUser.getIdToken()
  if (get(store, 'state.user.role', undefined) === 'admin') {
    const supplantedUser = get(store, 'state.supplantedUser', undefined)
    if (supplantedUser) {
      config.headers['supplanted-userId'] = supplantedUser._id
    }
  }
  return config
})

request.interceptors.response.use(
  (response) => handleResponse(null, response),
  (err) => handleResponse(err, err.response)
)

/**
 * Response handler
 * @param  {Error} err
 * @param  {Object} AJAX response
 * @return {Promise}
 */
function handleResponse(err, response) {
  if (err) {
    // user status limits error
    const errResponseDataName = get(err, 'response.data.name', '')
    if (
      err.response &&
      err.response.status === 403 &&
      errResponseDataName === 'users.statusLimitsReached'
    ) {
      store.dispatch('SET_USER_STATUS_LIMITS_ERROR', errResponseDataName)
    }

    // session expired
    if (err.response && err.response.status === 401) {
      store.dispatch('LOGOUT', { loginError: 'sessionExpired' })
    }

    return Promise.reject(err.response)
  }
  return new Promise((resolve) => {
    setTimeout(() => resolve(response), 0)
  })
}

/*******************************
 * PING
 *******************************/
export function ping() {
  return request({
    method: 'get',
    url: `/ping`,
  })
}

/*******************************
 * ADMIN - USERS
 *******************************/
export function adminGetUsers(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/user?${queryString}`,
  })
}
export function adminGetUserSummary(userId) {
  return request({
    method: 'get',
    url: `/admin/user/${userId}/summary`,
  })
}
export function adminUpdateUser(userId, data) {
  return request({
    method: 'put',
    url: `/admin/user/${userId}`,
    data,
  })
}
export function adminDeleteUser(userId) {
  return request({
    method: 'delete',
    url: `/admin/user/${userId}`,
  })
}
export function adminAddReferralCode(userId, referralCode) {
  return request({
    method: 'post',
    url: `/admin/user/${userId}/referral-code`,
    data: {
      referralCode,
    },
  })
}

/*******************************
 * ADMIN - CANCELLATION
 *******************************/
export function adminGetCancellations(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/cancellation?${queryString}`,
  })
}

/*******************************
 * ADMIN - KEEPFREEREASON
 *******************************/
export function adminGetKeepFreeReasons(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/keepFreeReason?${queryString}`,
  })
}

/*******************************
 * ADMIN - IMPORT
 *******************************/
export function adminGetImports(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/import?${queryString}`,
  })
}

/*******************************
 * ADMIN - MARKETS
 *******************************/
export function adminGetMarkets(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/market?${queryString}`,
  })
}
export function adminSetMarketAsPending(id) {
  return request({
    method: 'put',
    url: `/admin/market/${id}/pending`,
  })
}
export function adminUpdateMarket(id, data) {
  return request({
    method: 'put',
    url: `/admin/market/${id}`,
    data,
  })
}

/*******************************
 * ADMIN - POSITIONS
 *******************************/
export function adminGetPositions(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/admin/position?${queryString}`,
  })
}
export function adminPositionCoreRecreate(id) {
  return request({
    method: 'post',
    url: `/admin/position/${id}/core-recreate`,
  })
}

/*******************************
 * User
 *******************************/
export function getUser({ uid }) {
  return request({
    method: 'get',
    url: `/user?uid=${uid}`,
  })
}
export function createUser({ uid, data }) {
  return request({
    method: 'post',
    url: `/user?uid=${uid}`,
    data,
  })
}
export function updateUser({ uid, data }) {
  return request({
    method: 'put',
    url: `/user?uid=${uid}`,
    data,
  })
}
export function deleteUser({ uid, tokenExUser }) {
  return request({
    method: 'delete',
    url: `/user?uid=${uid}`,
    headers: {
      authorization: tokenExUser,
    },
  })
}
export function welcomed({ uid, data }) {
  return request({
    method: 'post',
    url: `/user/welcomed?uid=${uid}`,
    data,
  })
}

export function getUserOptIn({ uid }) {
  return request({
    method: 'get',
    url: `/user/opt-in?uid=${uid}`,
  })
}
export function updateUserOptIn({ uid, data }) {
  return request({
    method: 'post',
    url: `/user/opt-in?uid=${uid}`,
    data,
  })
}
export function addReferralCode({ uid, referralCode }) {
  return request({
    method: 'post',
    url: `/user/referral-code?uid=${uid}`,
    data: {
      referralCode,
    },
  })
}

/*******************************
 * User Referral
 *******************************/
export function getReferredUsers(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/referral/referred-users?${queryString}`,
  })
}

/*******************************
 * Account
 *******************************/
export function getAllowedPlans() {
  return request({
    method: 'get',
    url: `/account/allowed-plans`,
  })
}
export function customerPortal() {
  return request({
    method: 'post',
    url: `/account/customer-portal`,
  })
}
export function checkoutSession(data) {
  return request({
    method: 'post',
    url: `/account/checkout-session`,
    data,
  })
}

/*******************************
 * Cancellation
 *******************************/
export function createCancellation(data) {
  return request({
    method: 'post',
    url: '/cancellation',
    data,
  })
}

/*******************************
 * Keep Free Reason
 *******************************/
export function canCreateKeepFreeReason() {
  return request({
    method: 'get',
    url: `/keepFreeReason/can-create`,
  })
}
export function createKeepFreeReason(data) {
  return request({
    method: 'post',
    url: '/keepFreeReason',
    data,
  })
}

/*******************************
 * Networth
 *******************************/
export function getUserNetWorth() {
  return request({
    method: 'get',
    url: `/netWorth`,
  })
}
export function updateUserNetWorth({ data }) {
  return request({
    method: 'put',
    url: `/netWorth`,
    data,
  })
}

/*******************************
 * Tax
 *******************************/
export function getTaxSummary({ doc, startDate, endDate }) {
  const queryString = qs.stringify({ startDate, endDate })
  return request({
    method: 'get',
    url: `/tax/summary?${queryString}`,
  })
}
export function getTaxSummaryCsv({ doc, startDate, endDate, section }) {
  const queryString = qs.stringify({ startDate, endDate, section })
  return request({
    method: 'get',
    url: `/tax/summary/csv?${queryString}`,
  })
}

export function getPortfolioEndOfYear({ year, positionFilter }) {
  const queryString = qs.stringify({ year, positionFilter })
  return request({
    method: 'get',
    url: `/tax/portfolio-eoy?${queryString}`,
  })
}
export function getPortfolioEndOfYearCsv({ year, positionFilter, broker }) {
  const queryString = qs.stringify({ year, positionFilter, broker })
  return request({
    method: 'get',
    url: `/tax/portfolio-eoy/csv?${queryString}`,
  })
}
export function getTaxDoc({ doc, startDate, endDate }) {
  const queryString = qs.stringify({ startDate, endDate })
  return request({
    method: 'get',
    url: `/tax/doc/${doc}?${queryString}`,
  })
}
export function getD6Years() {
  return request({
    method: 'get',
    url: `/tax/doc/d6/years`,
  })
}
export function getD6({ year }) {
  const queryString = qs.stringify({ year })
  return request({
    method: 'get',
    url: `/tax/doc/d6?${queryString}`,
  })
}

/*******************************
 * Broker Accounts
 *******************************/
export function getBrokers() {
  return request({
    method: 'get',
    url: '/broker',
  })
}
export function createBroker(data) {
  return request({
    method: 'post',
    url: '/broker',
    data,
  })
}
export function editBroker(id, data) {
  return request({
    method: 'put',
    url: `/broker/${id}`,
    data,
  })
}
export function deleteBroker(broker) {
  return request({
    method: 'delete',
    url: `/broker/${broker._id}`,
  })
}

export function getBrokersActivePositions() {
  return request({
    method: 'get',
    url: '/broker/active',
  })
}

export function getBrokersDetail() {
  return request({
    method: 'get',
    url: '/broker/detail',
  })
}
/*******************************
 * Dividends
 *******************************/
export function getDividends(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/dividend?${queryString}`,
  })
}
export function previewDividend({ position, dividend, broker }) {
  return request({
    method: 'post',
    url: `/dividend/preview`,
    data: {
      position,
      dividend,
      broker,
    },
  })
}
export function createDividend(dividend) {
  return request({
    method: 'post',
    url: '/dividend',
    data: dividend,
  })
}
export function editDividend(id, dividend) {
  return request({
    method: 'put',
    url: `/dividend/${id}`,
    data: dividend,
  })
}
export function deleteDividend(dividend) {
  return request({
    method: 'delete',
    url: `/dividend/${dividend._id}`,
  })
}

export function getDividendSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/dividend/summary?${queryString}`,
  })
}

export function getDividendSummaryCsv(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/dividend/summary/csv?${queryString}`,
  })
}

export function getDividendTopSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/dividend/top-summary?${queryString}`,
  })
}

export function exportDividendsCsv({ filter }) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/dividend/export/csv?${filter}`,
  })
}

/*******************************
 * Orders
 *******************************/
export function getOrders(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order?${queryString}`,
  })
}

export function previewOrder({ position, order, type }) {
  return request({
    method: 'post',
    url: `/order/${type}/preview`,
    data: {
      position,
      order,
    },
  })
}
export function createOrder({ order, type }) {
  return request({
    method: 'post',
    url: `/order/${type}`,
    data: order,
  })
}
export function editOrder({ _id, order, type }) {
  return request({
    method: 'put',
    url: `/order/${type}/${_id}`,
    data: order,
  })
}
export function deleteOrder({ order }) {
  return request({
    method: 'delete',
    url: `/order/${order.type}/${order._id}`,
  })
}

export function getOrderProfit(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/profit?${queryString}`,
  })
}

export function getOrderSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/summary?${queryString}`,
  })
}

export function getScripSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/scrip/summary?${queryString}`,
  })
}

export function getOrderProfitSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/profit/summary?${queryString}`,
  })
}

export function exportOrderProfitSummaryCsv(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/profit/summary/csv?${queryString}`,
  })
}

export function getOrderProfitComparison(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/profit/comparison?${queryString}`,
  })
}

export function getOrderTopSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/order/top-summary?${queryString}`,
  })
}

export function exportOrdersCsv({ filter }) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/order/export/csv?${filter}`,
  })
}

export function exportOrderProfitCsv({ filter }) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/order/profit/export/csv?${filter}`,
  })
}

export function closeOptionOrder({ order }) {
  return request({
    method: 'post',
    url: `/order/${order.type}/${order._id}/close`,
  })
}

/*******************************
 * Core Orders
 *******************************/
export function getCoreOrders(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/coreOrder?${queryString}`,
  })
}

/*******************************
 * Positions
 *******************************/

export function getPositions(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position?${queryString}`,
  })
}

export function downloadPositionsCsv({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/position/csv?${filter}`,
  })
}
export function createPosition(position) {
  return request({
    method: 'post',
    url: '/position',
    data: position,
  })
}
export function editPosition(id, position) {
  return request({
    method: 'put',
    url: `/position/${id}`,
    data: position,
  })
}
export function deletePosition(position) {
  return request({
    method: 'delete',
    url: `/position/${position._id}`,
  })
}

export function getPosition(positionId) {
  return request({
    method: 'get',
    url: `/position/${positionId}`,
  })
}

export function getPositionsLean({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({ method: 'get', url: `/position/lean?${filter}` })
}

export function getPositionSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position/summary?${queryString}`,
  })
}
export function getPositionSummaryAndDetail(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position/summary-detail?${queryString}`,
  })
}

export function getPositionPerformance({ type, queryString }) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position/performance/${type}?${queryString}`,
  })
}
export function getPositionPerformanceCsv({ type, queryString }) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position/performance/${type}/csv?${queryString}`,
  })
}

export function getPositionTopSummary(queryString) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/position/top-summary?${queryString}`,
  })
}

/*******************************
 * Watches
 *******************************/
export function getWatches({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/watch?${filter}`,
  })
}
export function createWatch(watch) {
  return request({
    method: 'post',
    url: '/watch',
    data: watch,
  })
}
export function editWatch(id, watch) {
  return request({
    method: 'put',
    url: `/watch/${id}`,
    data: watch,
  })
}
export function deleteWatch(watch) {
  return request({
    method: 'delete',
    url: `/watch/${watch._id}`,
  })
}
export function getWatchesLean({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/watch/lean?${filter}`,
  })
}
export function getWatchTopSummary({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/watch/top-summary?${filter}`,
  })
}

/*******************************
 * Summary
 *******************************/
export function getSummary({ type, queryString }) {
  queryString = qs.stringify(queryString)
  return request({
    method: 'get',
    url: `/summary/${type}?${queryString}`,
  })
}

/*******************************
 * Import
 *******************************/
export function getImports({ filter } = {}) {
  filter = qs.stringify(filter)
  return request({
    method: 'get',
    url: `/import?${filter}`,
  })
}
export function getImport(_id) {
  return request({
    method: 'get',
    url: `/import/${_id}`,
  })
}
export function importCsv({
  type,
  broker,
  file,
  notCheckDuplicates,
  createBuysFromExternalTransfers,
}) {
  const formData = new FormData()
  formData.append('type', type)
  formData.append('broker', broker)
  formData.append('file', file)
  if (notCheckDuplicates) formData.append('notCheckDuplicates', notCheckDuplicates)
  if (createBuysFromExternalTransfers)
    formData.append('createBuysFromExternalTransfers', createBuysFromExternalTransfers)

  return request({
    method: 'post',
    url: `/import/csv`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}
export function completeImport({ _id, dict }) {
  return request({
    method: 'post',
    url: `import/${_id}/complete`,
    data: {
      dict,
    },
  })
}
export function undoImport({ _id }) {
  return request({
    method: 'post',
    url: `import/${_id}/undo`,
  })
}
export function cancelImport({ _id }) {
  return request({
    method: 'post',
    url: `import/${_id}/cancel`,
  })
}
